// Places a topic's location commonly names, keyed by their normalized name
// (see TopicLogic.timezoneForPlace), with the IANA zone they keep. The edit
// forms use it to default a topic's time zone from its location. Places
// not listed leave the zone as it is. A name several well-known places share
// across zones is listed bare for the best-known one and with its state for
// the others ("Portland, Maine"), or left out when none clearly dominates.

export const PLACE_TIMEZONES: Readonly<Record<string, string>> = {
  // United States
  'new york': 'America/New_York',
  'new york city': 'America/New_York',
  nyc: 'America/New_York',
  brooklyn: 'America/New_York',
  boston: 'America/New_York',
  philadelphia: 'America/New_York',
  pittsburgh: 'America/New_York',
  baltimore: 'America/New_York',
  washington: 'America/New_York',
  'washington dc': 'America/New_York',
  'washington d c': 'America/New_York',
  richmond: 'America/New_York',
  norfolk: 'America/New_York',
  raleigh: 'America/New_York',
  charlotte: 'America/New_York',
  atlanta: 'America/New_York',
  savannah: 'America/New_York',
  jacksonville: 'America/New_York',
  orlando: 'America/New_York',
  tampa: 'America/New_York',
  miami: 'America/New_York',
  tallahassee: 'America/New_York',
  detroit: 'America/Detroit',
  cleveland: 'America/New_York',
  columbus: 'America/New_York',
  cincinnati: 'America/New_York',
  indianapolis: 'America/Indiana/Indianapolis',
  louisville: 'America/Kentucky/Louisville',
  buffalo: 'America/New_York',
  albany: 'America/New_York',
  hartford: 'America/New_York',
  providence: 'America/New_York',
  portland: 'America/Los_Angeles',
  'portland me': 'America/New_York',
  'portland maine': 'America/New_York',
  burlington: 'America/New_York',
  chicago: 'America/Chicago',
  milwaukee: 'America/Chicago',
  madison: 'America/Chicago',
  minneapolis: 'America/Chicago',
  'st paul': 'America/Chicago',
  'saint paul': 'America/Chicago',
  'st louis': 'America/Chicago',
  'saint louis': 'America/Chicago',
  'kansas city': 'America/Chicago',
  omaha: 'America/Chicago',
  'des moines': 'America/Chicago',
  memphis: 'America/Chicago',
  nashville: 'America/Chicago',
  'new orleans': 'America/Chicago',
  'baton rouge': 'America/Chicago',
  'little rock': 'America/Chicago',
  'oklahoma city': 'America/Chicago',
  tulsa: 'America/Chicago',
  dallas: 'America/Chicago',
  'fort worth': 'America/Chicago',
  houston: 'America/Chicago',
  austin: 'America/Chicago',
  'san antonio': 'America/Chicago',
  'el paso': 'America/Denver',
  denver: 'America/Denver',
  boulder: 'America/Denver',
  'colorado springs': 'America/Denver',
  'salt lake city': 'America/Denver',
  albuquerque: 'America/Denver',
  'santa fe': 'America/Denver',
  boise: 'America/Boise',
  cheyenne: 'America/Denver',
  billings: 'America/Denver',
  phoenix: 'America/Phoenix',
  tucson: 'America/Phoenix',
  'las vegas': 'America/Los_Angeles',
  reno: 'America/Los_Angeles',
  'los angeles': 'America/Los_Angeles',
  'san diego': 'America/Los_Angeles',
  'san francisco': 'America/Los_Angeles',
  oakland: 'America/Los_Angeles',
  'san jose': 'America/Los_Angeles',
  sacramento: 'America/Los_Angeles',
  fresno: 'America/Los_Angeles',
  seattle: 'America/Los_Angeles',
  tacoma: 'America/Los_Angeles',
  spokane: 'America/Los_Angeles',
  anchorage: 'America/Anchorage',
  juneau: 'America/Juneau',
  fairbanks: 'America/Anchorage',
  honolulu: 'Pacific/Honolulu',
  'san juan': 'America/Puerto_Rico',
  hagatna: 'Pacific/Guam',

  // Canada and Mexico
  toronto: 'America/Toronto',
  ottawa: 'America/Toronto',
  montreal: 'America/Toronto',
  'quebec city': 'America/Toronto',
  halifax: 'America/Halifax',
  winnipeg: 'America/Winnipeg',
  calgary: 'America/Edmonton',
  edmonton: 'America/Edmonton',
  vancouver: 'America/Vancouver',
  'mexico city': 'America/Mexico_City',
  guadalajara: 'America/Mexico_City',
  monterrey: 'America/Monterrey',
  tijuana: 'America/Tijuana',

  // Elsewhere
  london: 'Europe/London',
  dublin: 'Europe/Dublin',
  paris: 'Europe/Paris',
  brussels: 'Europe/Brussels',
  amsterdam: 'Europe/Amsterdam',
  berlin: 'Europe/Berlin',
  frankfurt: 'Europe/Berlin',
  munich: 'Europe/Berlin',
  geneva: 'Europe/Zurich',
  zurich: 'Europe/Zurich',
  vienna: 'Europe/Vienna',
  rome: 'Europe/Rome',
  milan: 'Europe/Rome',
  madrid: 'Europe/Madrid',
  barcelona: 'Europe/Madrid',
  lisbon: 'Europe/Lisbon',
  stockholm: 'Europe/Stockholm',
  oslo: 'Europe/Oslo',
  copenhagen: 'Europe/Copenhagen',
  helsinki: 'Europe/Helsinki',
  warsaw: 'Europe/Warsaw',
  prague: 'Europe/Prague',
  budapest: 'Europe/Budapest',
  athens: 'Europe/Athens',
  istanbul: 'Europe/Istanbul',
  kyiv: 'Europe/Kyiv',
  kiev: 'Europe/Kyiv',
  moscow: 'Europe/Moscow',
  jerusalem: 'Asia/Jerusalem',
  'tel aviv': 'Asia/Jerusalem',
  gaza: 'Asia/Gaza',
  beirut: 'Asia/Beirut',
  cairo: 'Africa/Cairo',
  dubai: 'Asia/Dubai',
  riyadh: 'Asia/Riyadh',
  tehran: 'Asia/Tehran',
  baghdad: 'Asia/Baghdad',
  kabul: 'Asia/Kabul',
  karachi: 'Asia/Karachi',
  'new delhi': 'Asia/Kolkata',
  delhi: 'Asia/Kolkata',
  mumbai: 'Asia/Kolkata',
  bangalore: 'Asia/Kolkata',
  bengaluru: 'Asia/Kolkata',
  dhaka: 'Asia/Dhaka',
  bangkok: 'Asia/Bangkok',
  hanoi: 'Asia/Bangkok',
  singapore: 'Asia/Singapore',
  'kuala lumpur': 'Asia/Kuala_Lumpur',
  jakarta: 'Asia/Jakarta',
  manila: 'Asia/Manila',
  'hong kong': 'Asia/Hong_Kong',
  beijing: 'Asia/Shanghai',
  shanghai: 'Asia/Shanghai',
  taipei: 'Asia/Taipei',
  seoul: 'Asia/Seoul',
  tokyo: 'Asia/Tokyo',
  osaka: 'Asia/Tokyo',
  sydney: 'Australia/Sydney',
  melbourne: 'Australia/Melbourne',
  brisbane: 'Australia/Brisbane',
  perth: 'Australia/Perth',
  auckland: 'Pacific/Auckland',
  wellington: 'Pacific/Auckland',
  nairobi: 'Africa/Nairobi',
  lagos: 'Africa/Lagos',
  johannesburg: 'Africa/Johannesburg',
  'cape town': 'Africa/Johannesburg',
  'sao paulo': 'America/Sao_Paulo',
  'rio de janeiro': 'America/Sao_Paulo',
  'buenos aires': 'America/Argentina/Buenos_Aires',
  santiago: 'America/Santiago',
  lima: 'America/Lima',
  bogota: 'America/Bogota',
  caracas: 'America/Caracas',
  havana: 'America/Havana',
};
